import type { ReactNode } from 'react'

import Badge from '@components/atoms/Badge'
import cn from '@components/utils/cn'
import type { IconTypes } from '@components/utils/Icon'

import { type RunStatus, SHORT_STATUS_LABELS, STATUS_LABELS } from './sampleData'

export { Card, CardHeading } from '@components/imports/Card'

/** Alert children do not inherit a size, so every body needs `tw:text-sm`. */
export function AlertText({ children }: { children: ReactNode }) {
  return <span className="tw:text-sm">{children}</span>
}

const STATUS_COLORS: Record<RunStatus, { color: 'gray' | 'info' | 'success' | 'warning' | 'error'; icon: IconTypes }> =
  {
    queued: { color: 'gray', icon: 'clock' },
    working: { color: 'info', icon: 'refresh' },
    completed: { color: 'success', icon: 'check-circle' },
    completed_with_failures: { color: 'warning', icon: 'alert-triangle' },
    failed: { color: 'error', icon: 'x-circle' },
  }

/** Status is never colour only: the label and icon carry it too. */
export function StatusBadge({ status, short = false }: { status: RunStatus; short?: boolean }) {
  const { color, icon } = STATUS_COLORS[status]
  return (
    <Badge color={color} shape="rounded" leadingIcon={icon}>
      {(short ? SHORT_STATUS_LABELS : STATUS_LABELS)[status]}
    </Badge>
  )
}

/** The optional review extension: a run whose rows to review someone has marked as reviewed. */
export function ReviewedBadge() {
  return (
    <Badge color="gray" shape="rounded" leadingIcon="check-done-01">
      Reviewed
    </Badge>
  )
}

/** A big number with a label under it, for the counts on an import summary. */
export function CountTile({
  value,
  label,
  tone = 'default',
  muted,
}: {
  value: number
  label: string
  tone?: 'default' | 'warning' | 'error'
  muted?: boolean
}) {
  // warning-700 and error-700 are the lightest shades that pass 4.5:1 on a -50 tint.
  const tones = {
    default: 'tw:text-gray-cool-900',
    warning: 'tw:text-warning-700',
    error: 'tw:text-error-700',
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-5 tw:py-4">
      <span className={cn('tw:text-2xl tw:font-bold tw:tabular-nums', muted ? 'tw:text-gray-cool-400' : tones[tone])}>
        {value.toLocaleString()}
      </span>
      <span className="tw:text-xs tw:text-gray-cool-600">{label}</span>
    </div>
  )
}

export const inlineLinkClasses =
  'tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current'
